import { useState, useEffect } from 'react';
import clip from 'text-clipper';
import { safeParseHtml } from '~/utils/safeParseHtml';
import { linkifyUrls } from '~/utils/linkifyUrls';
import Check from '~/components/Check/Check';
import ViewMessageModal from '~/components/Messages/modals/ViewMessage';
import NotesModal from '~/components/Common/NotesModal';
import { formatDateIfValid } from '~/utils';
import {
    updateMessage,
    deleteMessage,
    updateCampaignRequestNote,
    deleteCampaignRequestNote,
    fetchCampaignRequestNoteEditHistory,
} from '~/services/campaign';
import { campaignRequestIdOf } from '~/utils/notes';
import { toast } from 'react-hot-toast';
import { useAuth } from '~/context/AuthProvider';
import { ContactAttempt } from '~/services/contactAttempts';
import { useContactAttempts } from '~/hooks/useContactAttempts';
import ContactAttemptsLoadError from '~/components/Common/ContactAttemptsLoadError';
import AddNoteInlineForm, { ADD_MSG_TYPE } from '../forms/AddNoteInlineForm';
import { FaPlus, FaUser } from 'react-icons/fa6';
import { FiEye, FiEdit2, FiUser } from 'react-icons/fi';
import EditNoteModal from '~/components/Common/EditNoteModal';
import EditContactAttemptModal from '~/components/Common/EditContactAttemptModal';

interface Note {
    id: number;
    created_date: string;
    created_by_name: string;
    note: string;
    tags?: string;
    pinned_flag?: boolean;
    todo_flag?: boolean;
    completed_by_name?: string;
    completed_date?: string | null;
    assignment?: string;
    assignee?: string;
    deleted_date?: string | null;
}

interface Props {
    notes: Note[];
    campaign: any;
    campaignRequest?: {
        id: number;
        initial_first_name?: string;
        initial_last_name?: string;
        patient_first_name?: string;
        patient_middle_name?: string;
        patient_last_name?: string;
        created_date?: string;
        notes?: string | null;
    };
    setNotes: any;
    loadingNotes?: boolean;
}

type HistoryItem = {
    id: string;
    date: Date;
    type: 'contact_attempt' | 'note' | 'todo';
    data: ContactAttempt | Note;
};

const ContactHistoryTab: React.FC<Props> = ({
    notes = [],
    campaign,
    campaignRequest,
    setNotes,
    loadingNotes = false,
}) => {
    const { person } = useAuth();
    const {
        contactAttempts,
        setContactAttempts,
        loadingAttempts,
        attemptsFailed,
        loadContactAttempts,
    } = useContactAttempts(campaignRequest?.id);
    const [notesModal, setNotesModal] = useState<{
        isOpen: boolean;
        note: Note | null;
    }>({ isOpen: false, note: null });
    const [editModal, setEditModal] = useState<{
        isOpen: boolean;
        note: Note | null;
    }>({ isOpen: false, note: null });
    const [editAttemptModal, setEditAttemptModal] = useState<{
        isOpen: boolean;
        attempt: ContactAttempt | null;
    }>({ isOpen: false, attempt: null });
    const [activeForm, setActiveForm] = useState<'todo' | 'note' | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const todos = notes.filter((note) => note.todo_flag);
    const justNotes = notes.filter((note) => !note.todo_flag);
    const viewCrId = notesModal.note ? campaignRequestIdOf(notesModal.note) : null;
    const editCrId = editModal.note ? campaignRequestIdOf(editModal.note) : null;

    // Reset to page 1 when notes are added/removed
    useEffect(() => {
        setCurrentPage(1);
    }, [notes.length]);

    const clientName = campaignRequest
        ? [
              campaignRequest?.patient_first_name,
              campaignRequest?.patient_middle_name,
              campaignRequest?.patient_last_name,
          ]
              .filter((e) => e)
              .join(' ') || 'Client'
        : 'Client';

    // This list mixes the campaign's Messages with its request's
    // CampaignRequestNotes, whose ids come from a different table and can
    // collide, so match (and route) by source as well as id.
    const sameNote = (note: any, id: number, campaignRequestId?: number | null) =>
        note.id === id && campaignRequestIdOf(note) === (campaignRequestId ?? null);

    const handleUpdateMessage = async (
        id: number,
        payload: any,
        campaignRequestId?: number | null,
    ) => {
        try {
            const res = campaignRequestId
                ? await updateCampaignRequestNote(campaignRequestId, id, payload)
                : await updateMessage(id, payload);
            if (res.status === 200) {
                toast.success('Todo updated successfully');
                setNotes((prevNotes: any[]) =>
                    prevNotes.map((note: any) => {
                        if (sameNote(note, id, campaignRequestId)) {
                            const updatedNote = { ...note, ...payload };

                            if (payload.completed && !note.completed_date) {
                                updatedNote.completed_date =
                                    new Date().toISOString();
                                updatedNote.completed_by_name =
                                    person?.first_name && person?.last_name
                                        ? `${person.first_name} ${person.last_name}`
                                        : 'Current User';
                            }

                            return updatedNote;
                        }
                        return note;
                    }),
                );
            } else {
                toast.error('Failed to update todo');
            }
        } catch (error) {
            console.error('Failed to update message:', error);
            toast.error('Failed to update todo');
        }
    };

    const handleDeleteMessage = async (
        id: number,
        campaignRequestId?: number | null,
    ) => {
        try {
            const res = campaignRequestId
                ? await deleteCampaignRequestNote(campaignRequestId, id)
                : await deleteMessage(id);

            if (res.status === 200 || res.status === 204) {
                toast.success('Todo deleted successfully');
                setNotes((prevNotes: any[]) => {
                    const filteredNotes = prevNotes.filter(
                        (note: any) => !sameNote(note, id, campaignRequestId),
                    );
                    return filteredNotes;
                });
            } else {
                toast.error('Failed to delete todo');
            }
        } catch (error) {
            console.error('Failed to delete message:', error);
            toast.error('Failed to delete todo');
        }
    };

    // Build unified history
    const unifiedHistory: HistoryItem[] = [];

    // Add contact attempts
    contactAttempts.forEach((attempt) => {
        unifiedHistory.push({
            id: `contact-${attempt.id}`,
            date: new Date(attempt.attempt_datetime),
            type: 'contact_attempt',
            data: attempt,
        });
    });

    // Add notes
    justNotes.forEach((note) => {
        unifiedHistory.push({
            id: `note-${note.id}`,
            date: new Date(note.created_date),
            type: 'note',
            data: note,
        });
    });

    // Add todos
    todos.forEach((todo) => {
        unifiedHistory.push({
            id: `todo-${todo.id}`,
            date: new Date(todo.created_date),
            type: 'todo',
            data: todo,
        });
    });

    // Sort by date (newest first)
    unifiedHistory.sort((a, b) => b.date.getTime() - a.date.getTime());

    const totalCount = unifiedHistory.length;
    const totalPages = Math.ceil(totalCount / itemsPerPage);

    // Calculate pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedHistory = unifiedHistory.slice(startIndex, endIndex);

    // Reset to page 1 if current page exceeds total pages
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1);
        }
    }, [currentPage, totalPages]);

    return (
        <div className='p-1'>
            <div className='mb-5 flex flex-wrap items-center justify-between gap-2'>
                <h2 className='mb-2 text-[22px]/[28px] font-bold'>
                    Campaign Notes ({totalCount})
                </h2>
                <div className='flex gap-2'>
                    <button
                        onClick={() =>
                            setActiveForm(activeForm === 'todo' ? null : 'todo')
                        }
                        className='inline-flex shrink items-center justify-center gap-1 whitespace-nowrap rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[12px] py-[6px] text-[11px]/[14px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#E6A830] hover:bg-[#E6A830] sm:gap-2 sm:px-[20px] sm:py-[7px] sm:text-[13px]/[16px]'
                    >
                        <FaPlus className='flex-shrink-0 text-[10px] sm:text-[12px]' />
                        Add To do
                    </button>
                    <button
                        onClick={() =>
                            setActiveForm(activeForm === 'note' ? null : 'note')
                        }
                        className='inline-flex shrink items-center justify-center gap-1 whitespace-nowrap rounded-full border-[2px] border-black bg-white px-[12px] py-[6px] text-[11px]/[14px] font-bold uppercase text-black transition-colors duration-500 hover:bg-gray-100 sm:gap-2 sm:px-[20px] sm:py-[7px] sm:text-[13px]/[16px]'
                    >
                        <FaPlus className='flex-shrink-0 text-[10px] sm:text-[12px]' />
                        Add Note
                    </button>
                </div>
            </div>

            {activeForm === 'todo' && (
                <AddNoteInlineForm
                    type={ADD_MSG_TYPE.ONLY_TODO}
                    campaign={campaign}
                    campaignRequest={campaignRequest}
                    setNotes={setNotes}
                    onClose={() => setActiveForm(null)}
                />
            )}

            {activeForm === 'note' && (
                <AddNoteInlineForm
                    type={ADD_MSG_TYPE.ONLY_NOTE}
                    campaign={campaign}
                    campaignRequest={campaignRequest}
                    setNotes={setNotes}
                    onClose={() => setActiveForm(null)}
                />
            )}

            {attemptsFailed && !loadingAttempts && (
                <ContactAttemptsLoadError onRetry={loadContactAttempts} />
            )}

            {loadingAttempts || loadingNotes ? (
                <div className='flex items-center justify-center py-8'>
                    <div className='text-gray-500'>Loading history...</div>
                </div>
            ) : totalCount === 0 && !attemptsFailed ? (
                <div className='flex items-center justify-center py-8'>
                    <div className='text-gray-500'>
                        No history found. Add the first entry!
                    </div>
                </div>
            ) : (
                <>
                    <div className='mt-3 space-y-3 break-words'>
                        {paginatedHistory.map((item) => {
                            if (item.type === 'contact_attempt') {
                                const attempt = item.data as ContactAttempt;
                                const date = new Date(attempt.attempt_datetime);
                                return (
                                    <div key={item.id}>
                                        <div className='flex items-center justify-between'>
                                            <div className='text-[12px]/[16px]'>
                                                {date.toLocaleDateString(
                                                    'en-US',
                                                    {
                                                        year: '2-digit',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                    },
                                                )}{' '}
                                                {date.toLocaleTimeString(
                                                    'en-US',
                                                    {
                                                        hour: 'numeric',
                                                        minute: '2-digit',
                                                        hour12: true,
                                                    },
                                                )}{' '}
                                                - Contact Attempt
                                            </div>
                                            <div className='flex items-center gap-2'>
                                                <div className='text-[12px]/[16px] font-bold'>
                                                    {attempt.person_name ||
                                                        'Unknown'}
                                                </div>
                                                <button
                                                    onClick={() =>
                                                        setEditAttemptModal({
                                                            isOpen: true,
                                                            attempt,
                                                        })
                                                    }
                                                    title='Edit contact attempt'
                                                >
                                                    <FiEdit2 className='text-primary' />
                                                </button>
                                            </div>
                                        </div>
                                        <div className='bg-[#F4F4F5] p-2 text-[14px]/[20px]'>
                                            {attempt.note || (
                                                <em className='text-gray-500'>
                                                    No note provided
                                                </em>
                                            )}
                                        </div>
                                        <div className='text-right text-[12px]/[16px] text-[#949599]'>
                                            {attempt.method_display}
                                        </div>
                                    </div>
                                );
                            } else if (item.type === 'todo') {
                                const todo = item.data as Note;
                                const hasNotes = todo.note.length > 0;
                                const handleNotesClick = () => {
                                    if (hasNotes) {
                                        setNotesModal({
                                            isOpen: true,
                                            note: todo,
                                        });
                                    }
                                };

                                return (
                                    <div key={item.id}>
                                        <div className='flex items-center justify-between gap-2'>
                                            <div className='flex items-center gap-1'>
                                                {todo?.completed_date ? (
                                                    <Check />
                                                ) : (
                                                    <FaUser
                                                        className='h-4 w-4'
                                                        style={{
                                                            color: '#f97316',
                                                        }}
                                                    />
                                                )}
                                                <div className='text-[12px]/[16px]'>
                                                    {formatDateIfValid(
                                                        todo.created_date,
                                                        'MM/dd/yy h:mm a',
                                                    )}{' '}
                                                    - To Do
                                                </div>
                                            </div>
                                            <div className='flex items-center gap-2'>
                                                <div className='text-[12px]/[16px] font-bold'>
                                                    {todo.created_by_name}
                                                </div>
                                                <ViewMessageModal
                                                    message={{
                                                        id: todo.id,
                                                        type: 'TODO',
                                                        date: todo.created_date,
                                                        for: '',
                                                        campaign:
                                                            campaign?.campaign_name ||
                                                            '',
                                                        message: todo.note,
                                                        isPinned:
                                                            todo.pinned_flag ||
                                                            false,
                                                        created_by_name:
                                                            todo.created_by_name,
                                                        completed_by_name:
                                                            todo.completed_by_name ||
                                                            '',
                                                        completed_date:
                                                            todo.completed_date ||
                                                            null,
                                                        campaign_id:
                                                            campaign?.campaign_id ||
                                                            0,
                                                        assignee:
                                                            todo.assignee || '',
                                                        campaign_request_id:
                                                            campaignRequestIdOf(
                                                                todo,
                                                            ),
                                                    }}
                                                    onDelete={
                                                        handleDeleteMessage
                                                    }
                                                    updateMessage={
                                                        handleUpdateMessage
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className='cursor-pointer bg-[#F4F4F5] p-2 text-[14px]/[20px] transition-colors hover:text-primary [&_a]:text-primary [&_a]:underline'
                                            onClick={handleNotesClick}
                                            title={
                                                hasNotes
                                                    ? 'Click to view full notes'
                                                    : ''
                                            }
                                        >
                                            {safeParseHtml(
                                                linkifyUrls(
                                                    clip(todo.note, 140, {
                                                        html: true,
                                                        maxLines: 5,
                                                    }),
                                                ),
                                            )}
                                        </div>
                                        {todo.assignee && (
                                            <div className='mt-1 text-[12px]/[16px] text-[#949599]'>
                                                Assigned to: {todo.assignee}
                                            </div>
                                        )}
                                    </div>
                                );
                            } else {
                                // note
                                const note = item.data as Note;
                                const hasNotes = note.note.length > 0;
                                const handleNotesClick = () => {
                                    if (hasNotes) {
                                        setNotesModal({
                                            isOpen: true,
                                            note: note,
                                        });
                                    }
                                };

                                return (
                                    <div key={item.id}>
                                        <div className='flex items-center justify-between'>
                                            <div className='flex items-center gap-1 text-[12px]/[16px]'>
                                                {note.todo_flag &&
                                                    (note.completed_date ? (
                                                        <Check className='text-green-600 h-4 w-4' />
                                                    ) : (
                                                        <FiUser className='h-4 w-4 text-gray-500' />
                                                    ))}
                                                {formatDateIfValid(
                                                    note.created_date,
                                                    'MM/dd/yy h:mm a',
                                                )}{' '}
                                                -{' '}
                                                {note.todo_flag
                                                    ? 'To Do'
                                                    : 'Note'}
                                            </div>
                                            <div className='flex items-center gap-2'>
                                                <div className='text-[12px]/[16px] font-bold'>
                                                    {note.created_by_name}
                                                </div>
                                                <button
                                                    onClick={handleNotesClick}
                                                    title='View note'
                                                >
                                                    <FiEye className='text-primary' />
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        setEditModal({
                                                            isOpen: true,
                                                            note: note,
                                                        })
                                                    }
                                                    title='Edit note'
                                                >
                                                    <FiEdit2 className='text-primary' />
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            className='cursor-pointer bg-[#F4F4F5] p-2 text-[14px]/[20px] transition-colors hover:text-primary [&_a]:text-primary [&_a]:underline'
                                            onClick={handleNotesClick}
                                            title={
                                                hasNotes
                                                    ? 'Click to view full notes'
                                                    : ''
                                            }
                                        >
                                            {safeParseHtml(
                                                linkifyUrls(
                                                    clip(note.note, 140, {
                                                        html: true,
                                                        maxLines: 5,
                                                    }),
                                                ),
                                            )}
                                        </div>
                                        {note.assignee && (
                                            <div className='mt-1 text-[12px]/[16px] text-[#949599]'>
                                                Assigned to: {note.assignee}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                        })}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className='mt-6 flex flex-col items-center gap-3 border-t border-gray-200 pt-4'>
                            <div className='flex gap-2'>
                                <button
                                    onClick={() =>
                                        setCurrentPage((prev) =>
                                            Math.max(1, prev - 1),
                                        )
                                    }
                                    disabled={currentPage === 1}
                                    className='rounded border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                                >
                                    &lt;
                                </button>

                                <div className='flex gap-1'>
                                    {Array.from(
                                        { length: totalPages },
                                        (_, i) => i + 1,
                                    ).map((page) => {
                                        // Show first page, last page, current page, and pages around current
                                        const showPage =
                                            page === 1 ||
                                            page === totalPages ||
                                            (page >= currentPage - 1 &&
                                                page <= currentPage + 1);

                                        const showEllipsis =
                                            (page === currentPage - 2 &&
                                                currentPage > 3) ||
                                            (page === currentPage + 2 &&
                                                currentPage < totalPages - 2);

                                        if (showEllipsis) {
                                            return (
                                                <span
                                                    key={page}
                                                    className='px-2 py-1 text-gray-500'
                                                >
                                                    ...
                                                </span>
                                            );
                                        }

                                        if (!showPage) return null;

                                        return (
                                            <button
                                                key={page}
                                                onClick={() =>
                                                    setCurrentPage(page)
                                                }
                                                className={`rounded px-3 py-1 text-sm font-medium ${
                                                    currentPage === page
                                                        ? 'bg-primary text-white'
                                                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() =>
                                        setCurrentPage((prev) =>
                                            Math.min(totalPages, prev + 1),
                                        )
                                    }
                                    disabled={currentPage === totalPages}
                                    className='rounded border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                                >
                                    &gt;
                                </button>
                            </div>
                            <div className='text-sm text-gray-700'>
                                Showing {startIndex + 1} to{' '}
                                {Math.min(endIndex, totalCount)} of {totalCount}{' '}
                                entries
                            </div>
                        </div>
                    )}
                </>
            )}

            <NotesModal
                isOpen={notesModal.isOpen}
                note={notesModal.note}
                campaignId={
                    viewCrId
                        ? undefined
                        : campaign?.campaign_id || campaign?.id || 0
                }
                onClose={() => setNotesModal({ isOpen: false, note: null })}
                onPinToggle={
                    viewCrId
                        ? undefined
                        : (updatedNote: Note) => {
                              setNotes((prevNotes: Note[]) =>
                                  prevNotes.map((n) =>
                                      sameNote(n, updatedNote.id, null)
                                          ? updatedNote
                                          : n,
                                  ),
                              );
                          }
                }
                onDelete={(noteId: number) => {
                    setNotes((prevNotes: Note[]) =>
                        prevNotes.filter((n) => !sameNote(n, noteId, viewCrId)),
                    );
                }}
                customDeleteFn={
                    viewCrId
                        ? (noteId) => deleteCampaignRequestNote(viewCrId, noteId)
                        : undefined
                }
                customFetchHistoryFn={
                    viewCrId
                        ? (noteId) =>
                              fetchCampaignRequestNoteEditHistory(viewCrId, noteId)
                        : undefined
                }
            />

            <EditNoteModal
                isOpen={editModal.isOpen}
                note={editModal.note}
                campaignId={
                    editCrId ? 0 : campaign?.campaign_id || campaign?.id || 0
                }
                onClose={() => setEditModal({ isOpen: false, note: null })}
                onSave={(updatedNote: Note) => {
                    setNotes((prevNotes: Note[]) =>
                        prevNotes.map((n) =>
                            sameNote(n, updatedNote.id, editCrId) ? updatedNote : n,
                        ),
                    );
                }}
                hidePinToggle={!!editCrId}
                customUpdateFn={
                    editCrId
                        ? (noteId, payload) =>
                              updateCampaignRequestNote(editCrId, noteId, payload)
                        : undefined
                }
                customFetchHistoryFn={
                    editCrId
                        ? (noteId) =>
                              fetchCampaignRequestNoteEditHistory(editCrId, noteId)
                        : undefined
                }
            />

            <EditContactAttemptModal
                isOpen={editAttemptModal.isOpen}
                attempt={editAttemptModal.attempt}
                onClose={() =>
                    setEditAttemptModal({ isOpen: false, attempt: null })
                }
                onSave={(updated) => {
                    setContactAttempts((prev) =>
                        prev.map((a) => (a.id === updated.id ? updated : a)),
                    );
                }}
            />
        </div>
    );
};

export default ContactHistoryTab;
